import { NavLink } from "react-router";
import type { LayoutStyle } from "@/lib/layout-style";
import { cn } from "@/lib/cn";

export type NavItemVisual = {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  accent?: string;
  dataTour?: string;
};

/** Item de navegação — renderiza diferente conforme o layout style:
 *  - compact: denso, ícone pequeno, padding mínimo
 *  - classic: espaçoso (padrão antigo)
 *  - colorful: ícone em pill colorido por item, visual SaaS moderno */
export function NavItemButton({
  item,
  active,
  collapsed,
  style,
  onClick,
}: {
  item: NavItemVisual;
  active: boolean;
  collapsed: boolean;
  style: LayoutStyle;
  onClick?: () => void;
}) {
  const Icon = item.icon;
  const accent = item.accent ?? "#2457ff";

  if (style === "colorful") {
    return (
      <NavLink
        to={item.href}
        onClick={onClick}
        title={collapsed ? item.label : undefined}
        data-tour={item.dataTour}
        className={cn(
          "group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] transition-colors",
          active
            ? "bg-zinc-50 font-semibold text-zinc-900 dark:bg-muted/40 dark:text-foreground"
            : "text-zinc-700 hover:bg-zinc-50 dark:text-foreground/80 dark:hover:bg-muted",
          collapsed && "md:justify-center md:px-1.5",
        )}
      >
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-transform group-hover:scale-105",
            active && "ring-2 ring-offset-1 ring-offset-white dark:ring-offset-card",
          )}
          style={{
            backgroundColor: `${accent}1a`,
            color: accent,
            ...(active
              ? ({ ["--tw-ring-color" as string]: accent } as React.CSSProperties)
              : {}),
          }}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className={cn("truncate", collapsed && "md:hidden")}>{item.label}</span>
      </NavLink>
    );
  }

  if (style === "classic") {
    return (
      <NavLink
        to={item.href}
        onClick={onClick}
        title={collapsed ? item.label : undefined}
        data-tour={item.dataTour}
        className={cn(
          "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
          active
            ? "bg-brand-50 font-medium text-brand-900 dark:bg-primary/10 dark:text-primary"
            : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground",
          collapsed && "md:justify-center md:px-2",
        )}
      >
        <Icon
          className={cn(
            "h-[18px] w-[18px] shrink-0",
            active
              ? "text-brand-700 dark:text-primary"
              : "text-zinc-400 group-hover:text-zinc-600 dark:text-muted-foreground/70 dark:group-hover:text-foreground",
          )}
        />
        <span className={cn("truncate", collapsed && "md:hidden")}>{item.label}</span>
      </NavLink>
    );
  }

  // compact (default)
  return (
    <NavLink
      to={item.href}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      data-tour={item.dataTour}
      className={cn(
        "group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] leading-tight transition-colors",
        active
          ? "bg-brand-50 font-semibold text-brand-900 dark:bg-primary/10 dark:text-primary"
          : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground",
        collapsed && "md:justify-center md:px-2",
      )}
    >
      <Icon
        className={cn(
          "h-4 w-4 shrink-0",
          active
            ? "text-brand-700 dark:text-primary"
            : "text-zinc-400 group-hover:text-zinc-600 dark:text-muted-foreground/70 dark:group-hover:text-foreground",
        )}
      />
      <span className={cn("truncate", collapsed && "md:hidden")}>{item.label}</span>
    </NavLink>
  );
}
